import React from 'react';
import { useHistory, useParams } from 'react-router';
import { Box } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

import { convertPublicProfileToSelectedProviderType, SelectedProvider } from '@uplift/pages';
import { useL10n } from '@uplift/hooks';
import { UpliftThemeProvider, Breadcrumbs, Breadcrumb, Button, Loader } from '@uplift/ui-v2';
import { ROUTING_CONFIG } from '@uplift/constants';
import { MainLayout } from '../../components';
import { CampusPlan, getProviderPublicProfile } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { Link } from 'react-router-dom';
import { ProviderMedicalRole } from '@uplift/types';
import { useQuery } from '@tanstack/react-query';
import { useCurrentStudentDetails, useMatchPreferenceQuery, usePatientMatchedProvider, useStates, useUserInsurancesQuery } from '@uplift/queries';


export const CareTeamProviderPage: React.VFC = () => {
  const l10n = useL10n();
  const me = useCurrentuser();
  const currentStudentDetails = useCurrentStudentDetails();
  const { classes } = useCareTeamProviderStyles();
  const history = useHistory();
  const params = useParams<{id: string}>();
  const { data: publicProfile, isInitialLoading: isPublicProfileLoading } = useQuery({
    queryKey: [ 'provider-public-profile', params.id ],
    queryFn: async () => {
      const response = await getProviderPublicProfile(params.id);

      return response.data;
    },
  });
  const isTherapist = publicProfile?.user?.medicalRole === ProviderMedicalRole.therapist;
  const { data: insurances, isLoading: isInsurancesLoading } = useUserInsurancesQuery(me?.id);
  const { states } = useStates();
  const primaryInsuranceCompany = React.useMemo(() => {
    if (isInsurancesLoading) {
      return null;
    }
    const primaryInsuranceCompany = insurances.find(i => !i.insurance.isSecondaryInsurance)?.insuranceCompany;
    const insuranceCompanyNames = publicProfile?.supportedInsuranceCompanies.map(insurance => insurance.shortName);

    if (insuranceCompanyNames?.includes(primaryInsuranceCompany?.shortName)) {
      return primaryInsuranceCompany;
    }
    return null;
  }, [ insurances, isInsurancesLoading, publicProfile?.supportedInsuranceCompanies ]);

  const hideAcceptedInsurances = React.useMemo(() => {
    const plan = currentStudentDetails.higherEdOrg?.contract?.plan;

    if (plan?.type === CampusPlan.flex && (plan.therapy || plan.psychiatry)) {
      return true;
    }
    if (plan?.type === CampusPlan.complete) {
      return true;
    }

    if (insurances?.length && !primaryInsuranceCompany) {
      return true;
    }

    return false;
  }, [ insurances, primaryInsuranceCompany, currentStudentDetails ]);

  const matchedInsuranceCompany =  React.useMemo(() => {
    if (!primaryInsuranceCompany) {
      return undefined;
    }
    return {
      ...primaryInsuranceCompany,
      logo: primaryInsuranceCompany.logoUrl,
      metaGrouping: undefined
    };
  }, [ primaryInsuranceCompany ]);
  const { data: providerMatch, isLoading: isProviderMatchLoading } = usePatientMatchedProvider(me.id, params.id, [ 'active' ], { enabled: !!me.id });
  const matchId = providerMatch?.match?.id;
  const { data: matchPreferences, isLoading: isMatchPreferenceLoading } = useMatchPreferenceQuery(matchId, { enabled: !!matchId });
  const mountRef = React.useRef(false);

  React.useEffect(() => {
    if (!mountRef.current) {
      mountRef.current = true;
      return;
    }
    if (!isPublicProfileLoading && !publicProfile?.user) {
      history.replace(ROUTING_CONFIG.careTeam);
    }
  }, [ isPublicProfileLoading, publicProfile?.user ]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isPublicProfileLoading || isProviderMatchLoading || isMatchPreferenceLoading || isInsurancesLoading) {
    return (
      <MainLayout>
        <UpliftThemeProvider>
          <Box my={20}>
            <Loader color="secondary" />
          </Box>
        </UpliftThemeProvider>
      </MainLayout>
    );
  }

  const providerName = publicProfile.nameDisplay.full;
  const selectedProvider = convertPublicProfileToSelectedProviderType(publicProfile, states);

  return (
    <MainLayout containerClassName={classes.mainLayoutContainer}>
      <UpliftThemeProvider>
        <Box>
          <Box mb={{ xs: 2, sm: 0 }} display="flex" justifyContent="space-between" flexWrap="wrap" gap="1rem">
            <Breadcrumbs aria-label="breadcrumb">
              <Breadcrumb to={ROUTING_CONFIG.careTeam}>{l10n.pages.yourCareTeam}</Breadcrumb>
              <Breadcrumb currentPage>{providerName}</Breadcrumb>
            </Breadcrumbs>
            {matchId && isTherapist && (
              <Button
                component={Link}
                to={`/rematching/${matchId}`}
                color="secondary"
                variant="outlined"
              >
                {l10n.requestRematch.findANewTherapist}
              </Button>
            )}
          </Box>
          <Box mx={{ xs: -2, md: -10 }}>
            <SelectedProvider
              stateId={matchPreferences.locationState}
              isScrollableView
              hideScheduler
              matchedInsuranceCompany={matchedInsuranceCompany}
              selectedProvider={selectedProvider}
              hideAcceptedInsurances={hideAcceptedInsurances}
              classes={{
                mainContainer: classes.selectedProviderMainContainer,
              }}
            />
          </Box>
        </Box>
      </UpliftThemeProvider>
    </MainLayout>
  );
};

const useCareTeamProviderStyles = makeStyles({ name: 'CareTeamProvider' })(theme => ({
  selectedProviderMainContainer: {
    margin: 0,

    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 3),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 10),
    },
  },
  mainLayoutContainer: {
    paddingBottom: 0,
  }
}));
